import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Phone, Mail, MapPin, CheckCircle, Recycle, Clock, Award, ArrowRight, ArrowUp, ChevronLeft, ChevronRight, X, ZoomIn, Menu } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Hardcoded services data - images générées par IA (Gemini Nano Banana) - libres de droit
const HARDCODED_SERVICES = [
  {
    id: "1",
    title: "Enlèvement d'encombrants",
    description: "Évacuation complète de vos objets volumineux : meubles, électroménagers, matelas, cartons. Tri, chargement et évacuation conformes aux normes belges.",
    image: "/api/uploads/ai_encombrants_pile.png",
    order: 1
  },
  {
    id: "2",
    title: "Vide maison & appartement",
    description: "Succession, déménagement, rénovation : nous vidons intégralement votre bien. Restitution d'un espace propre, prêt à louer ou à vendre.",
    image: "/api/uploads/ai_vide_maison_apres.png",
    order: 2
  },
  {
    id: "3",
    title: "Débarras cave, grenier & garage",
    description: "Libérez les espaces les plus encombrés de votre habitation. Accès difficile, charges lourdes, gravats : notre équipement professionnel s'en charge.",
    image: "/api/uploads/ai_garage_avant_apres.png",
    order: 3
  },
  {
    id: "4",
    title: "Destruction documents confidentiels",
    description: "Broyage sécurisé sur site ou en centre agréé. Archives, dossiers RH, données comptables : certificat de destruction RGPD délivré après intervention.",
    image: "/api/uploads/ai_documents_destruction.png",
    order: 4
  }
];

// Hardcoded gallery data - images générées par IA (Gemini Nano Banana) - 100% libres de droit
const HARDCODED_GALLERY = [
  { id: "1", title: "Cave désencombrée", description: "Cave et sous-sol libérés de meubles, cartons et objets accumulés", image: "/api/uploads/ai_cave_encombrants.png" },
  { id: "2", title: "Grenier vidé", description: "Évacuation de malles, vieux meubles et cartons sur plusieurs niveaux", image: "/api/uploads/ai_grenier_encombrants.png" },
  { id: "3", title: "Avant / Après — Garage débarrassé", description: "Outils obsolètes, vélos et encombrants évacués, garage rendu propre", image: "/api/uploads/ai_garage_avant_apres.png" },
  { id: "4", title: "Maison entièrement vidée", description: "Restitution d'un espace propre, prêt à louer ou à vendre", image: "/api/uploads/ai_vide_maison_apres.png" },
  { id: "5", title: "Encombrants prêts à évacuer", description: "Mobilier, matelas et équipements rassemblés pour collecte", image: "/api/uploads/ai_encombrants_pile.png" },
  { id: "6", title: "Destruction d'archives confidentielles", description: "Broyage sécurisé sur site avec certificat RGPD", image: "/api/uploads/ai_documents_destruction.png" },
  { id: "7", title: "Nettoyage post-débarras", description: "Pièce remise propre après évacuation complète des encombrants", image: "/api/uploads/ai_nettoyage_apres.png" }
];

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    postalCode: '',
    subject: '',
    message: ''
  });
  const [scrollY, setScrollY] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [services, setServices] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Gallery slider states
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const loadData = useCallback(async () => {
    // Use hardcoded data directly (module-level constants)
    setServices(HARDCODED_SERVICES);
    setGalleryItems(HARDCODED_GALLERY);
    setLoading(false);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      setShowScrollTop(window.scrollY > 400);
    };
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);
    
    // Load data from backend initially
    loadData();
    
    // Refresh data every 10 seconds for real-time sync
    const refreshInterval = setInterval(() => {
      loadData();
    }, 10000);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
      clearInterval(refreshInterval);
    };
  }, [loadData]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Send to backend
      await axios.post(`${API}/contact`, formData);
      toast({
        title: "Message envoyé!",
        description: "Nous vous répondrons dans les 24 heures.",
      });
      setFormData({ name: '', email: '', phone: '', postalCode: '', subject: '', message: '' });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error submitting form:', error);
      }
      toast({
        title: "Erreur",
        description: "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive"
      });
    }
  };

  const values = [
    {
      icon: <CheckCircle className="w-12 h-12" />,
      title: "Transparence Totale",
      description: "Devis clair, détaillé et sans surprise. Chaque prestation est expliquée à l'avance : pas de frais cachés, pas de mauvaise surprise. Vous savez exactement ce que vous payez."
    },
    {
      icon: <Recycle className="w-12 h-12" />,
      title: "Démarche Éco-Responsable",
      description: "Partenariats officiels avec les centres de recyclage agréés en Belgique. Jusqu'à 80% de vos encombrants revalorisés : don aux associations, recyclage matière, réemploi solidaire."
    },
    {
      icon: <Clock className="w-12 h-12" />,
      title: "Intervention Express",
      description: "Devis sous 2 heures, intervention possible dès 48h. Notre équipe mobile couvre toute la Belgique, du lundi au samedi, pour répondre à vos urgences débarras."
    },
    {
      icon: <Award className="w-12 h-12" />,
      title: "Équipe Certifiée",
      description: "Personnel formé, assuré et déclaré. BELKGROUP est une entreprise enregistrée à la TVA belge avec assurance responsabilité civile professionnelle pour votre tranquillité."
    }
  ];

  // Gallery slider functions
  const nextSlide = useCallback(() => {
    if (galleryItems.length > 0) {
      setCurrentSlide((prev) => (prev + 1) % galleryItems.length);
    }
  }, [galleryItems.length]);

  const prevSlide = useCallback(() => {
    if (galleryItems.length > 0) {
      setCurrentSlide((prev) => (prev - 1 + galleryItems.length) % galleryItems.length);
    }
  }, [galleryItems.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  const openZoom = (image) => {
    setZoomedImage(image);
    setIsZoomed(true);
    setIsAutoPlaying(false);
  };

  const closeZoom = () => {
    setIsZoomed(false);
    setZoomedImage(null);
    setIsAutoPlaying(true);
  };

  // Auto-play slider
  useEffect(() => {
    if (isAutoPlaying && galleryItems.length > 1) {
      const interval = setInterval(nextSlide, 4000);
      return () => clearInterval(interval);
    }
  }, [isAutoPlaying, galleryItems.length, nextSlide]);

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      {/* Professional Background Image */}
      <div 
        className="fixed inset-0 z-0 opacity-[0.03]"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1581573833610-487d80de9aab)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      />
      
      {/* Gradient Overlay */}
      <div className="fixed inset-0 z-0 bg-gradient-to-b from-white via-transparent to-white opacity-90"></div>
      
      {/* Animated Background Waves */}
      <div className="fixed inset-0 pointer-events-none z-0 opacity-30">
        <svg className="absolute top-0 left-0 w-full" viewBox="0 0 1440 320" preserveAspectRatio="none">
          <path 
            fill="url(#gradient1)" 
            fillOpacity="1" 
            d="M0,160L48,144C96,128,192,96,288,106.7C384,117,480,171,576,181.3C672,192,768,160,864,149.3C960,139,1056,149,1152,138.7C1248,128,1344,96,1392,80L1440,64L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"
            style={{ 
              transform: `translateY(${scrollY * 0.2}px)`,
              transition: 'transform 0.1s ease-out'
            }}
          >
            <animate attributeName="d" dur="10s" repeatCount="indefinite" values="
              M0,160L48,144C96,128,192,96,288,106.7C384,117,480,171,576,181.3C672,192,768,160,864,149.3C960,139,1056,149,1152,138.7C1248,128,1344,96,1392,80L1440,64L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z;
              M0,128L48,149.3C96,171,192,213,288,208C384,203,480,149,576,128C672,107,768,117,864,138.7C960,160,1056,192,1152,186.7C1248,181,1344,139,1392,117.3L1440,96L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z;
              M0,160L48,144C96,128,192,96,288,106.7C384,117,480,171,576,181.3C672,192,768,160,864,149.3C960,139,1056,149,1152,138.7C1248,128,1344,96,1392,80L1440,64L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z
            " />
          </path>
          <defs>
            <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" style={{ stopColor: '#06b6d4', stopOpacity: 0.4 }} />
              <stop offset="50%" style={{ stopColor: '#22d3ee', stopOpacity: 0.3 }} />
              <stop offset="100%" style={{ stopColor: '#06b6d4', stopOpacity: 0.4 }} />
            </linearGradient>
          </defs>
        </svg>
        <svg className="absolute bottom-0 left-0 w-full" viewBox="0 0 1440 320" preserveAspectRatio="none">
          <path 
            fill="url(#gradient2)" 
            fillOpacity="1" 
            d="M0,128L48,138.7C96,149,192,171,288,165.3C384,160,480,128,576,112C672,96,768,96,864,112C960,128,1056,160,1152,165.3C1248,171,1344,149,1392,138.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            style={{ 
              transform: `translateY(${-scrollY * 0.15}px)`,
              transition: 'transform 0.1s ease-out'
            }}
          >
            <animate attributeName="d" dur="12s" repeatCount="indefinite" values="
              M0,128L48,138.7C96,149,192,171,288,165.3C384,160,480,128,576,112C672,96,768,96,864,112C960,128,1056,160,1152,165.3C1248,171,1344,149,1392,138.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z;
              M0,160L48,170.7C96,181,192,203,288,197.3C384,192,480,160,576,138.7C672,117,768,107,864,128C960,149,1056,203,1152,208C1248,213,1344,171,1392,149.3L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z;
              M0,128L48,138.7C96,149,192,171,288,165.3C384,160,480,128,576,112C672,96,768,96,864,112C960,128,1056,160,1152,165.3C1248,171,1344,149,1392,138.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z
            " />
          </path>
          <defs>
            <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" style={{ stopColor: '#1e3a8a', stopOpacity: 0.5 }} />
              <stop offset="50%" style={{ stopColor: '#06b6d4', stopOpacity: 0.4 }} />
              <stop offset="100%" style={{ stopColor: '#1e3a8a', stopOpacity: 0.5 }} />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Floating particles */}
      <div className="fixed inset-0 pointer-events-none z-0">
        {[...Array(20)].map((_, i) => (
          <div
            key={`particle-${i}`}
            className="absolute w-2 h-2 bg-cyan-400 rounded-full opacity-20"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${5 + Math.random() * 10}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`
            }}
          />
        ))}
      </div>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrollY > 50 ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20 md:h-24">
            <div className="flex items-center group cursor-pointer">
              <img 
                src="https://customer-assets.emergentagent.com/job_debarras-maison-1/artifacts/ww2n76fa_Design%20sans%20titre%20%286%29.png"
                alt="BelkGroup - Débarras Bruxelles"
                className={`h-14 sm:h-16 md:h-20 w-auto object-contain transition-all duration-300
                  group-hover:scale-110
                  ${scrollY > 50 ? 'drop-shadow-lg' : 'drop-shadow-2xl'}`}
              />
            </div>
            <div className="hidden md:flex space-x-8">
              {['Nos valeurs', 'Services', 'Réalisations', 'Contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase().replace(' ', '-'))}
                  className={`font-medium transition-colors hover:text-cyan-500 ${
                    scrollY > 50 ? 'text-gray-700' : 'text-white'
                  }`}
                  data-testid={`nav-${item.toLowerCase().replace(' ', '-')}`}
                >
                  {item}
                </button>
              ))}
            </div>
            <Button 
              onClick={() => scrollToSection('contact')}
              className="hidden sm:flex bg-cyan-500 hover:bg-cyan-600 text-white"
              data-testid="cta-devis-gratuit"
            >
              Devis gratuit
            </Button>
            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`md:hidden p-2 rounded-lg transition-colors ${
                scrollY > 50 ? 'text-gray-800 hover:bg-gray-100' : 'text-white hover:bg-white/20'
              }`}
              aria-label="Menu"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        {/* Mobile menu panel */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white shadow-xl border-t border-gray-100 animate-in slide-in-from-top duration-200">
            <div className="px-4 py-4 space-y-1">
              {['Nos valeurs', 'Services', 'Réalisations', 'Contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => {
                    scrollToSection(item.toLowerCase().replace(' ', '-'));
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full text-left px-4 py-3 rounded-lg text-gray-800 font-medium hover:bg-cyan-50 hover:text-cyan-600 transition-colors"
                  data-testid={`mobile-nav-${item.toLowerCase().replace(' ', '-')}`}
                >
                  {item}
                </button>
              ))}
              <Button
                onClick={() => {
                  scrollToSection('contact');
                  setMobileMenuOpen(false);
                }}
                className="w-full mt-2 bg-cyan-500 hover:bg-cyan-600 text-white"
                data-testid="mobile-cta-devis-gratuit"
              >
                Devis gratuit
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(/api/uploads/ai_hero_encombrants.png)',
            transform: `translateY(${scrollY * 0.5}px)`,
            filter: 'brightness(0.5)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 via-cyan-900/70 to-blue-950/95"></div>
        </div>
        
        {/* Floating shapes with parallax */}
        <div 
          className="absolute top-20 left-10 w-20 h-20 bg-cyan-400/20 rounded-full blur-xl"
          style={{ transform: `translate(${mousePosition.x * 0.02}px, ${mousePosition.y * 0.02}px)` }}
        />
        <div 
          className="absolute bottom-40 right-20 w-32 h-32 bg-blue-500/20 rounded-full blur-xl"
          style={{ transform: `translate(${mousePosition.x * -0.03}px, ${mousePosition.y * -0.03}px)` }}
        />
        <div 
          className="absolute top-1/2 left-1/3 w-24 h-24 bg-cyan-300/20 rounded-full blur-xl"
          style={{ transform: `translate(${mousePosition.x * 0.015}px, ${mousePosition.y * 0.015}px)` }}
        />
        
        <div className="relative z-10 text-center text-white px-4 max-w-5xl mx-auto">
          <div className="inline-block px-4 py-1.5 mb-6 rounded-full bg-cyan-500/20 border border-cyan-400/40 backdrop-blur-sm">
            <p className="text-xs sm:text-sm font-semibold tracking-widest uppercase text-cyan-200">Débarras Bruxelles & Environs • Belgique</p>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold mb-6 animate-fade-in drop-shadow-lg leading-tight">
            Débarras d'encombrants{' '}<br className="hidden sm:block"/>
            <span className="text-cyan-400">à Bruxelles & environs</span>
          </h1>
          <p className="text-base sm:text-xl md:text-2xl mb-6 animate-fade-in-delay drop-shadow-md text-gray-100 px-2">
            Vide maison • Vide appartement • Cave • Grenier • Garage • Documents
          </p>
          <p className="text-sm sm:text-base md:text-lg mb-8 animate-fade-in-delay opacity-90 text-cyan-100 px-2">
            Devis sous 2h · Intervention dès 48h · 80% recyclé · TVA belge
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center animate-fade-in-delay-2 px-4">
            <Button 
              onClick={() => scrollToSection('contact')}
              size="lg" 
              className="w-full sm:w-auto bg-gradient-to-r from-cyan-500 to-cyan-600 text-white hover:from-cyan-600 hover:to-cyan-700 hover:scale-105 hover:shadow-2xl hover:shadow-cyan-500/50 text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 transition-all duration-500 transform relative overflow-hidden group"
              data-testid="hero-cta-devis"
            >
              <span className="relative z-10 flex items-center justify-center">
                Demandez votre devis
                <ArrowRight className="ml-2 group-hover:translate-x-2 transition-transform duration-300" />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </Button>
            <Button 
              onClick={() => scrollToSection('réalisations')}
              size="lg" 
              variant="outline" 
              className="w-full sm:w-auto border-2 border-white text-white hover:bg-white hover:text-cyan-600 hover:scale-105 hover:shadow-2xl text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 transition-all duration-500 backdrop-blur-sm"
              data-testid="hero-cta-realisations"
            >
              Voir nos réalisations
            </Button>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section id="nos-valeurs" className="py-20 bg-gradient-to-b from-gray-50 to-white relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
              style={{
                transform: scrollY > 500 ? 'translateY(0)' : 'translateY(50px)',
                opacity: scrollY > 500 ? 1 : 0,
                transition: 'all 0.8s ease-out'
              }}
            >
              Nos Valeurs
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-cyan-500 to-blue-900 mx-auto"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, idx) => (
              <Card 
                key={value.title} 
                className="group hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border-t-4 border-cyan-500 relative overflow-hidden transform hover:rotate-1"
                style={{
                  transform: scrollY > 500 ? 'translateY(0) scale(1)' : 'translateY(80px) scale(0.95)',
                  opacity: scrollY > 500 ? 1 : 0,
                  transition: `all 1s cubic-bezier(0.34, 1.56, 0.64, 1) ${idx * 0.1}s`
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-50 via-blue-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                <div className="absolute top-0 right-0 w-20 h-20 bg-cyan-400 rounded-full filter blur-3xl opacity-0 group-hover:opacity-30 transition-opacity duration-700"></div>
                <CardContent className="p-6 text-center relative z-10">
                  <div className="text-cyan-600 mb-4 flex justify-center group-hover:scale-125 group-hover:rotate-12 transition-all duration-700">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-cyan-600 transition-colors duration-300">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-white relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div
              style={{
                transform: scrollY > 1000 ? 'translateX(0)' : 'translateX(-100px)',
                opacity: scrollY > 1000 ? 1 : 0,
                transition: 'all 0.8s ease-out'
              }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
                Débarras d'encombrants à Bruxelles & environs
              </h2>
              <h3 className="text-xl sm:text-2xl font-semibold text-cyan-600 mb-3 sm:mb-4">
                Vide maison, cave, grenier et garage — Service rapide et écologique
              </h3>
              <p className="text-gray-600 mb-4">
                BELKGROUP est votre spécialiste du <strong>débarras d'encombrants à Bruxelles et environs</strong>. 
                Nous intervenons pour le <strong>vide maison complet</strong>, <strong>vide appartement</strong>, 
                débarras de <strong>cave</strong>, <strong>grenier</strong> et <strong>garage</strong>, 
                ainsi que pour la destruction sécurisée de documents confidentiels.
              </p>
              <p className="text-gray-600 mb-6">
                Succession à gérer, déménagement, rénovation ou simple besoin de libérer de l'espace : 
                notre équipe se déplace dans les 19 communes de Bruxelles 
                (<em>Anderlecht, Ixelles, Uccle, Schaerbeek, Saint-Gilles, Etterbeek, Woluwe, Forest, Molenbeek…</em>) 
                ainsi qu'en <strong>Brabant wallon</strong> (Waterloo, Wavre, Nivelles, La Hulpe…). 
                Tri, recyclage jusqu'à 80% et nettoyage final inclus.
              </p>
              <div className="flex flex-wrap gap-3 sm:gap-4">
                <div className="flex items-center space-x-2 group">
                  <CheckCircle className="w-5 h-5 text-green-600 group-hover:scale-125 transition-transform flex-shrink-0" />
                  <span className="text-sm sm:text-base text-gray-700">Entreprise déclarée & assurée</span>
                </div>
                <div className="flex items-center space-x-2 group">
                  <CheckCircle className="w-5 h-5 text-green-600 group-hover:scale-125 transition-transform flex-shrink-0" />
                  <span className="text-sm sm:text-base text-gray-700">Bruxelles + 19 communes</span>
                </div>
                <div className="flex items-center space-x-2 group">
                  <CheckCircle className="w-5 h-5 text-green-600 group-hover:scale-125 transition-transform flex-shrink-0" />
                  <span className="text-sm sm:text-base text-gray-700">Devis gratuit sous 2h</span>
                </div>
              </div>
            </div>
            <div 
              className="relative"
              style={{
                transform: scrollY > 1000 ? 'translateX(0) scale(1)' : 'translateX(100px) scale(0.9)',
                opacity: scrollY > 1000 ? 1 : 0,
                transition: 'all 0.8s ease-out'
              }}
            >
              <img 
                src="/api/uploads/ai_garage_avant_apres.png"
                alt="Avant / Après — Démonstration du résultat d'un débarras BelkGroup en Belgique"
                className="rounded-lg shadow-2xl hover:shadow-cyan-300 transition-shadow duration-500"
              />
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-br from-cyan-500 to-blue-900 text-white p-6 rounded-lg shadow-xl hover:scale-110 transition-transform duration-300 cursor-pointer">
                <p className="text-2xl font-bold">Devis sous 2h</p>
                <p className="text-sm">Réponse rapide garantie</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gradient-to-b from-gray-50 to-white relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
              style={{
                transform: scrollY > 1600 ? 'translateY(0)' : 'translateY(50px)',
                opacity: scrollY > 1600 ? 1 : 0,
                transition: 'all 0.8s ease-out'
              }}
            >
              Nos Services
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-cyan-500 to-blue-900 mx-auto"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {loading ? (
              // Loading skeleton
              [...Array(4)].map((_, idx) => (
                <Card key={`skeleton-${idx}`} className="overflow-hidden">
                  <div className="h-64 bg-gray-200 animate-pulse"></div>
                  <CardContent className="p-6">
                    <div className="h-6 bg-gray-200 rounded animate-pulse mb-3"></div>
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  </CardContent>
                </Card>
              ))
            ) : services.length === 0 ? (
              <div className="col-span-2 text-center py-12">
                <p className="text-gray-500">Aucun service disponible</p>
              </div>
            ) : (
              services.map((service, idx) => (
                <Card 
                  key={service.id || idx} 
                  className="group overflow-hidden hover:shadow-2xl transition-all duration-700 transform hover:-translate-y-3 hover:rotate-1 border-2 border-transparent hover:border-cyan-500"
                  style={{
                    transform: scrollY > 1700 ? 'translateY(0) rotateY(0deg)' : 'translateY(80px) rotateY(-15deg)',
                    opacity: scrollY > 1700 ? 1 : 0,
                    transition: `all 1s cubic-bezier(0.34, 1.56, 0.64, 1) ${idx * 0.15}s`
                  }}
                >
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-125 group-hover:rotate-3 transition-all duration-1000"
                      onError={(e) => {
                        e.target.src = 'https://placehold.co/800x400?text=Service+Image';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent group-hover:from-cyan-900/90 transition-all duration-700"></div>
                    <div className="absolute inset-0 bg-cyan-500/0 group-hover:bg-cyan-500/10 transition-colors duration-700"></div>
                    <div className="absolute bottom-4 left-4 right-4 transform translate-y-0 group-hover:-translate-y-2 transition-transform duration-500">
                      <h3 className="text-2xl font-bold text-white mb-2 group-hover:scale-105 transition-transform duration-300">{service.title}</h3>
                    </div>
                  </div>
                  <CardContent className="p-6 bg-gradient-to-br from-white to-gray-50">
                    <p className="text-gray-600">{service.description}</p>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Gallery Section - Modern Slider */}
      <section id="réalisations" className="py-20 bg-gradient-to-b from-gray-50 to-white relative z-10 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
              style={{
                transform: scrollY > 2800 ? 'translateY(0)' : 'translateY(50px)',
                opacity: scrollY > 2800 ? 1 : 0,
                transition: 'all 0.8s ease-out'
              }}
            >
              Nos Réalisations
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-cyan-500 to-blue-700 mx-auto mb-4"></div>
            <p className="text-gray-600 max-w-2xl mx-auto">Découvrez nos travaux de débarras et vide maison réalisés avec professionnalisme</p>
          </div>

          {loading ? (
            <div className="h-96 bg-gray-200 rounded-2xl animate-pulse"></div>
          ) : galleryItems.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Aucune réalisation disponible</p>
            </div>
          ) : (
            <div className="relative">
              {/* Main Slider */}
              <div className="relative h-[500px] md:h-[600px] rounded-3xl overflow-hidden shadow-2xl bg-gray-900">
                {galleryItems.map((img, idx) => {
                  const imageUrl = img.image;
                  
                  return (
                    <div
                      key={img.id || `slide-${idx}`}
                      className={`absolute inset-0 transition-all duration-700 ease-in-out ${
                        idx === currentSlide 
                          ? 'opacity-100 visible z-10' 
                          : 'opacity-0 invisible z-0'
                      }`}
                    >
                      <img 
                        src={imageUrl}
                        alt={img.title || `Réalisation ${idx + 1}`}
                        className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-700"
                        onClick={() => openZoom(imageUrl)}
                        onError={(e) => {
                          e.target.src = 'https://placehold.co/800x600?text=Image';
                        }}
                      />
                      {/* Gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                      
                      {/* Image info */}
                      <div className="absolute bottom-0 left-0 right-0 p-8">
                        <h3 className="text-white text-2xl md:text-3xl font-bold mb-2 drop-shadow-lg">
                          {img.title || 'Réalisation BelkGroup'}
                        </h3>
                        {img.description && (
                          <p className="text-white/80 text-lg drop-shadow-md">{img.description}</p>
                        )}
                      </div>

                      {/* Zoom button */}
                      <button 
                        onClick={(e) => { e.stopPropagation(); openZoom(imageUrl); }}
                        className="absolute top-6 right-6 w-12 h-12 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/40 transition-all duration-300 hover:scale-110"
                      >
                        <ZoomIn className="w-6 h-6 text-white" />
                      </button>
                    </div>
                  );
                })}

                {/* Navigation arrows */}
                <button 
                  onClick={() => { prevSlide(); setIsAutoPlaying(false); setTimeout(() => setIsAutoPlaying(true), 5000); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-14 h-14 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/40 transition-all duration-300 hover:scale-110 group"
                >
                  <ChevronLeft className="w-8 h-8 text-white group-hover:-translate-x-1 transition-transform" />
                </button>
                <button 
                  onClick={() => { nextSlide(); setIsAutoPlaying(false); setTimeout(() => setIsAutoPlaying(true), 5000); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-14 h-14 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/40 transition-all duration-300 hover:scale-110 group"
                >
                  <ChevronRight className="w-8 h-8 text-white group-hover:translate-x-1 transition-transform" />
                </button>
              </div>

              {/* Thumbnails */}
              <div className="flex justify-center gap-3 mt-6 flex-wrap">
                {galleryItems.map((img, idx) => {
                  const imageUrl = img.image;
                  
                  return (
                    <button
                      key={img.id || `thumb-${idx}`}
                      onClick={() => goToSlide(idx)}
                      className={`relative w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden transition-all duration-300 ${
                        idx === currentSlide 
                          ? 'ring-4 ring-cyan-500 scale-110 shadow-lg' 
                          : 'opacity-60 hover:opacity-100 hover:scale-105'
                      }`}
                    >
                      <img 
                        src={imageUrl}
                        alt={`Thumbnail ${idx + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'https://placehold.co/100x100?text=...';
                        }}
                      />
                    </button>
                  );
                })}
              </div>

              {/* Progress dots */}
              <div className="flex justify-center gap-2 mt-6">
                {galleryItems.map((img, idx) => (
                  <button
                    key={img.id || `dot-${idx}`}
                    onClick={() => goToSlide(idx)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      idx === currentSlide 
                        ? 'w-8 bg-cyan-500' 
                        : 'w-2 bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Zoom Modal */}
      {isZoomed && zoomedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={closeZoom}
        >
          <button 
            onClick={closeZoom}
            className="absolute top-6 right-6 w-12 h-12 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/20 transition-all duration-300 z-50"
          >
            <X className="w-8 h-8 text-white" />
          </button>
          
          <button 
            onClick={(e) => { e.stopPropagation(); prevSlide(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-50 w-14 h-14 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/20 transition-all duration-300"
          >
            <ChevronLeft className="w-8 h-8 text-white" />
          </button>
          
          <button 
            onClick={(e) => { e.stopPropagation(); nextSlide(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-50 w-14 h-14 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/20 transition-all duration-300"
          >
            <ChevronRight className="w-8 h-8 text-white" />
          </button>

          <img 
            src={zoomedImage}
            alt="Zoomed"
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl animate-in zoom-in-95 duration-300"
            onClick={(e) => e.stopPropagation()}
          />
          
          <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-sm">
            Cliquez en dehors de l'image ou sur ✕ pour fermer
          </p>
        </div>
      )}

      {/* Stats Section - Différenciation pro */}
      <section className="py-16 bg-gradient-to-r from-slate-900 via-blue-950 to-slate-900 relative z-10 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-400 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { value: "48h", label: "Délai d'intervention" },
              { value: "80%", label: "Encombrants recyclés" },
              { value: "100%", label: "Devis transparents" },
            ].map((stat) => (
              <div key={stat.label} className="text-center group">
                <p className="text-4xl md:text-6xl font-extrabold bg-gradient-to-br from-cyan-300 to-cyan-500 bg-clip-text text-transparent group-hover:scale-110 transition-transform duration-300">
                  {stat.value}
                </p>
                <p className="mt-2 text-sm md:text-base text-gray-300 uppercase tracking-wider font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-b from-white to-gray-50 relative z-10 overflow-hidden">
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 -left-20 w-96 h-96 bg-cyan-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
          <div className="absolute top-40 -right-20 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-20 left-1/2 w-96 h-96 bg-cyan-300 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 
              className="text-5xl md:text-6xl font-bold text-gray-900 mb-4"
              style={{
                transform: scrollY > 4200 ? 'translateY(0)' : 'translateY(50px)',
                opacity: scrollY > 4200 ? 1 : 0,
                transition: 'all 0.8s ease-out'
              }}
            >
              Demandez votre devis gratuit
            </h2>
            <div className="w-32 h-1.5 bg-gradient-to-r from-cyan-500 via-blue-600 to-cyan-500 mx-auto mb-6 rounded-full"></div>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Intervention rapide • Devis gratuit • Service professionnel
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form - Modern Design */}
            <div
              className="relative"
              style={{
                transform: scrollY > 4300 ? 'translateX(0) scale(1)' : 'translateX(-80px) scale(0.95)',
                opacity: scrollY > 4300 ? 1 : 0,
                transition: 'all 1s cubic-bezier(0.34, 1.56, 0.64, 1)'
              }}
            >
              <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 p-8 hover:shadow-cyan-500/20 transition-all duration-500">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Envoyez votre demande</h3>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-cyan-500 rounded-full"></div>
                      Votre nom complet
                    </label>
                    <Input
                      name="name"
                      placeholder="Jean Dupont"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="h-12 border-2 border-gray-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 rounded-xl transition-all duration-300 bg-white/50"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-cyan-500 rounded-full"></div>
                        Email
                      </label>
                      <Input
                        name="email"
                        type="email"
                        placeholder="jean@email.com"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="h-12 border-2 border-gray-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 rounded-xl transition-all duration-300 bg-white/50"
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-cyan-500 rounded-full"></div>
                        Téléphone
                      </label>
                      <Input
                        name="phone"
                        placeholder="+32 4XX XX XX XX"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="h-12 border-2 border-gray-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 rounded-xl transition-all duration-300 bg-white/50"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-cyan-500 rounded-full"></div>
                      Code postal
                    </label>
                    <Input
                      name="postalCode"
                      placeholder="1000"
                      value={formData.postalCode}
                      onChange={handleInputChange}
                      className="h-12 border-2 border-gray-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 rounded-xl transition-all duration-300 bg-white/50"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-cyan-500 rounded-full"></div>
                      Type de service
                    </label>
                    <Input
                      name="subject"
                      placeholder="Ex: Vide maison, Débarras cave..."
                      value={formData.subject}
                      onChange={handleInputChange}
                      className="h-12 border-2 border-gray-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 rounded-xl transition-all duration-300 bg-white/50"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-cyan-500 rounded-full"></div>
                      Décrivez votre projet
                    </label>
                    <Textarea
                      name="message"
                      placeholder="Décrivez en détail votre besoin de débarras..."
                      rows={5}
                      value={formData.message}
                      onChange={handleInputChange}
                      className="border-2 border-gray-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 rounded-xl transition-all duration-300 resize-none bg-white/50"
                      required
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full h-14 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white text-lg font-semibold rounded-xl shadow-lg hover:shadow-cyan-500/50 hover:scale-105 transition-all duration-300 group"
                  >
                    <span className="flex items-center justify-center gap-2">
                      Envoyer ma demande
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                    </span>
                  </Button>
                </form>
              </div>
            </div>
            
            {/* Contact Info - Modern Cards */}
            <div 
              className="space-y-6"
              style={{
                transform: scrollY > 4300 ? 'translateX(0) scale(1)' : 'translateX(80px) scale(0.95)',
                opacity: scrollY > 4300 ? 1 : 0,
                transition: 'all 1s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s'
              }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Nos coordonnées</h3>
              
              {/* Contact Info Cards */}
              <Card className="border-0 bg-white/80 backdrop-blur-xl shadow-xl hover:shadow-2xl hover:shadow-cyan-500/10 transition-all duration-500 mb-6 group overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full filter blur-3xl opacity-0 group-hover:opacity-20 transition-opacity duration-500"></div>
                <CardContent className="p-6 space-y-5 relative z-10">
                  <div className="flex items-start space-x-4 group/item hover:translate-x-2 transition-transform duration-300">
                    <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                      <MapPin className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 mb-1 text-lg">Adresse</p>
                      <p className="text-gray-600 leading-relaxed">Rue Royal 11<br />1000 Bruxelles, Belgique</p>
                    </div>
                  </div>
                  
                  <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
                  
                  <div className="flex items-start space-x-4 group/item hover:translate-x-2 transition-transform duration-300">
                    <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                      <Phone className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 mb-1 text-lg">Téléphone</p>
                      <a href="tel:+32493381189" className="text-cyan-600 hover:text-cyan-700 font-semibold text-lg">
                        +32(0)493 38 11 89
                      </a>
                    </div>
                  </div>
                  
                  <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
                  
                  <div className="flex items-start space-x-4 group/item hover:translate-x-2 transition-transform duration-300">
                    <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                      <Mail className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 mb-1 text-lg">Email</p>
                      <a href="mailto:info@belkgroup.be" className="text-cyan-600 hover:text-cyan-700 font-semibold">
                        info@belkgroup.be
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Hours Card */}
              <Card className="border-0 bg-gradient-to-br from-cyan-500 via-cyan-600 to-blue-700 text-white shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-500 overflow-hidden relative">
                <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full filter blur-3xl"></div>
                <CardContent className="p-8 relative z-10">
                  <div className="flex items-center gap-3 mb-6">
                    <Clock className="w-8 h-8" />
                    <h4 className="text-2xl font-bold">Horaires d'ouverture</h4>
                  </div>
                  <div className="space-y-3 text-base">
                    <div className="flex justify-between items-center bg-white/10 backdrop-blur-sm rounded-xl p-3">
                      <span className="font-medium">Lundi - Vendredi</span>
                      <span className="font-bold">08:00 – 18:00</span>
                    </div>
                    <div className="flex justify-between items-center bg-white/10 backdrop-blur-sm rounded-xl p-3">
                      <span className="font-medium">Samedi</span>
                      <span className="font-bold">08:00 – 12:00</span>
                    </div>
                  </div>
                  <div className="mt-6 p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                    <p className="text-sm font-semibold text-center">
                      ⚡ Réponse sous 24h garantie
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_debarras-maison-1/artifacts/ww2n76fa_Design%20sans%20titre%20%286%29.png"
                  alt="BelkGroup Cleaning Service Logo"
                  className="h-20 w-auto object-contain"
                />
              </div>
              <p className="text-gray-400 mb-4">
                Spécialiste en débarras d'encombrants et vide maison pour particuliers et professionnels.
              </p>
              <p className="text-gray-400 text-sm mb-4">
                <strong>TVA :</strong> BE 0728 836 818
              </p>
              <div className="flex space-x-4">
                <a 
                  href="https://www.facebook.com/profile.php?id=100069437707737" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-900 rounded-full flex items-center justify-center hover:scale-110 transition-all duration-300 cursor-pointer"
                >
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a 
                  href="https://www.instagram.com/belkgroup?igsh=MTMxM3MwdTA3cnZhdA==" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center hover:scale-110 transition-all duration-300 cursor-pointer"
                >
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li 
                  onClick={() => scrollToSection('services')}
                  className="hover:text-white transition-colors cursor-pointer"
                >
                  Débarras d'encombrants
                </li>
                <li 
                  onClick={() => scrollToSection('services')}
                  className="hover:text-white transition-colors cursor-pointer"
                >
                  Vide maison complet
                </li>
                <li 
                  onClick={() => scrollToSection('services')}
                  className="hover:text-white transition-colors cursor-pointer"
                >
                  Vide cave et grenier
                </li>
                <li 
                  onClick={() => scrollToSection('services')}
                  className="hover:text-white transition-colors cursor-pointer"
                >
                  Débarras de bureau
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Zones d'intervention</h4>
              <p className="text-gray-400 text-sm mb-2">Débarras à Bruxelles & environs :</p>
              <ul className="space-y-1 text-gray-400 text-sm">
                <li>Bruxelles-Capitale (19 communes)</li>
                <li>Anderlecht, Ixelles, Uccle, Schaerbeek</li>
                <li>Saint-Gilles, Etterbeek, Woluwe, Forest</li>
                <li>Brabant wallon : Waterloo, Wavre, Nivelles</li>
                <li>Brabant flamand & toute la Belgique</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400 text-sm">
            <p>© 2026 BELKGROUP - Tous droits réservés</p>
          </div>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-gradient-to-br from-cyan-500 to-blue-900 text-white p-4 rounded-full shadow-2xl hover:shadow-cyan-500/50 transition-all duration-300 z-50 hover:scale-125 animate-bounce"
          style={{ animation: 'pulse-glow 2s infinite' }}
        >
          <ArrowUp className="w-6 h-6" />
        </button>
      )}
    </div>
  );
};

export default Home;